class MainMenu {
  constructor(element, options = {
    mobileMediaQuery: '(max-width: 800px)',
  }) {
    // Elements
    this.header = element;
    this.menuBody = element.querySelector('[data-element="main-menu"]');
    this.menuBtn = element.querySelector('#menu-toggle');
    this.menuSubnavItems = [...element.querySelectorAll('[data-element="subnav-item"]')];
    this.menuSubnavBtns = [...element.querySelectorAll('[data-element="subnav-button"]')];
    this.menuCheckbox = element.querySelector('#menu-toggle');

    // Options
    this.options = options;

    this.timer;
    this.headerTimer;
    this.delay = 250;

    // Media query logic
    // grabs css custom variable for nav break from `_nav.scss`
    this.rootStyles = getComputedStyle(document.documentElement);
    this.navMediaQueryBreak = this.rootStyles.getPropertyValue('--nav-break');

    // set navbreak css custom property
    if (this.navMediaQueryBreak) this.options.mobileMediaQuery = `(max-width: ${this.navMediaQueryBreak})`;

    // Initial values for functions
    this.show = true;
    this.expandedSubNav = null;

    this.init();
  }

  /**
   * Initilization functions
   */
  init() {
    // manually bind functions to the class
    this.bindFunctions();

    // manually bind js events
    this.bindEvents();

    // necessary only if hamburger button or
    // toggle needs to work on mobile or a certain viewport
    this.watchMedia(this.options.mobileMediaQuery);
  }


  /**
   * @param {String} mobileMediaQuery - media query based on nav-break css custom property taken from `_nav.scss`
   * Sets initial mobile or desktop requirements on load,
   * and listens for viewport changes,
   * and runs mediaChange function
   */
  watchMedia(mobileMediaQuery) {
    const mediaQuery = window.matchMedia(mobileMediaQuery);

    // Call once to set initial state
    this.mediaChange(mediaQuery);

    mediaQuery.addEventListener('change', (event) => {
      this.mediaChange(event);
    });
  }

  /**
   * @param {Event} mobileMediaQuery - MediaQueryListEvent
   * Closes/hides nav if on mobile,
   * and removes or adds mouse events depending on viewports
   */
  mediaChange(mobileMediaQuery) {
    if (mobileMediaQuery.matches) {
      // Small screen

      this.initHideDropdowns('mobile');
      window.addEventListener('click', (event) => {
        this.onCloseNav(event, 'mobile');
      });

      window.addEventListener('keydown', (event) => {
        this.onCloseNav(event, 'mobile');
      });

      // if no subnavs exist, exit function
      if (!this.menuSubnavBtns) return;

      // if subnavs exist, remove mouseover functions since the assumption is
      // touchscreen for clicking
      this.menuBody.removeEventListener('mouseover', this.onMouseOverSubnav);
      this.menuBody.removeEventListener('mouseleave', this.onMouseLeaveSubnav);
    } else {
      // Large screen
      this.initHideDropdowns('desktop');

      window.addEventListener('click', (event) => {
        this.onCloseNav(event, 'desktop');
      });

      window.addEventListener('keydown', (event) => {
        this.onCloseNav(event, 'desktop');
      });

      this.menuBody.addEventListener('mouseover', this.onMouseOverSubnav);
      this.menuBody.addEventListener('mouseleave', this.onMouseLeaveSubnav);
    }
  }


  bindEvents() {
    this.header.addEventListener('mouseover', this.showHeaderClass);
    this.header.addEventListener('mouseleave', this.hideHeaderClass);
    this.menuBtn.addEventListener('click', this.toggleMenu);
    this.menuBody.addEventListener('click', this.onClickSubnav);
  }

  bindFunctions() {
    this.showHeaderClass = this.showHeaderClass.bind(this);
    this.hideHeaderClass = this.hideHeaderClass.bind(this);
    this.initHideDropdowns = this.initHideDropdowns.bind(this);
    this.toggleMenu = this.toggleMenu.bind(this);
    this.updateSubNav = this.updateSubNav.bind(this);
    this.updateSubNavButton = this.updateSubNavButton.bind(this);
    this.onMouseOverSubnav = this.onMouseOverSubnav.bind(this);
    this.onMouseLeaveSubnav = this.onMouseLeaveSubnav.bind(this);
    this.onClickSubnav = this.onClickSubnav.bind(this);
    this.onHandleDropdown = this.onHandleDropdown.bind(this);
    this.onCloseNav = this.onCloseNav.bind(this);
  }

  showHeaderClass() {
    clearTimeout(this.headerTimer);
    this.header.classList.add('is-hovered');
  }

  hideHeaderClass() {
    this.headerTimer = setTimeout(() => {
      this.header.classList.remove('is-hovered');
    }, this.delay);
  }

  /**
   * @param {String} mode - "mobile" | "desktop" decides which elements should be hidden based on viewport
   * Runs the main hide menu function
   * based on mode string
   * and hides subnavs if they exist
   */
  initHideDropdowns(mode) {
    // hide main nav
    this.hideMenu(mode);

    // hide subnavs
    if (this.menuSubnavBtns) {
      this.hideSubnavs();
    }

    // fade in nav to prevent flash content from subnavs on page load
    document.querySelectorAll('[data-element="main-menu-subnav"]').forEach((el) => {
      el.removeAttribute('style');
    });
  }

  /**
   * @param {String} mode - "mobile" | "desktop" decides which elements should be hidden based on viewport
   * hides what needs to be hidden for the menu dropdowns
   * based on mode string
   */
  hideMenu(mode) {
    // uncheck checkbox if checked
    // this is necessary if the nav and checkbox is checked
    // and the user goes to a larger viewport
    // and back to a smaller viewport, this resets the menu
    // to act as expected
    if (this.menuCheckbox.checked) this.menuCheckbox.checked = false;

    if (document.documentElement.classList.contains('menu-active')) document.documentElement.classList.remove('menu-active');

    if (mode == 'mobile') {
      this.menuBody.hidden = true;

      // hide subnavs
      if (this.menuSubnavBtns) {
        this.hideSubnavs();
      }
    } else if (mode == 'desktop') {
      this.menuBody.hidden = false;
      this.expandedSubNav = null;
    } else {
      this.menuBody.hidden = true;
      this.expandedSubNav = null;
    }

    this.menuBtn.setAttribute('aria-expanded', false);
    this.show = true;
  }

  /**
   * Hides all subnavs
   */
  hideSubnavs() {
    this.menuSubnavBtns.forEach((btn) => {
      btn.setAttribute('aria-expanded', false);
      btn.nextElementSibling.hidden = true;
    });
  }

  /**
   * Updates appropriate elements to show main menu
   */
  showMenu() {
    this.menuBody.hidden = false;
    this.menuBtn.setAttribute('aria-expanded', true);
    this.show = false;
  }

  /**
   * @param {HTMLElement} buttonToExpand - set to button to expand or null to just close previous
   * @param {Boolean} trap - is focus trap needed for next sibling
   */
  updateSubNav(buttonToExpand, trap) {
    // Close previously open sub nav
    this.updateSubNavButton(this.expandedSubNav, false, 'old');

    // Open new sub nav
    this.updateSubNavButton(buttonToExpand, true, 'new');

    // Is a focus trap needed?
    if (buttonToExpand && trap) {
      this.trapFocus(buttonToExpand.nextElementSibling);
    }

    // Save new reference to current button
    this.expandedSubNav = buttonToExpand;
  }


  /**
   * @param {HTMLElement} button - set to button to expand or null to just close previous
   * @param {Boolean} makeVisible - boolean to show or hide, if true, it expands for aria, and removes the hidden attribute from the subnav. If false, it does the opposite.
   * @param {String} item - previous or next item
   */
  updateSubNavButton(button, makeVisible) {
    if (!button) {
      return;
    }

    button.setAttribute('aria-expanded', makeVisible);
    button.nextElementSibling.hidden = !makeVisible;
  }

  /**
   * Removes active subnav once user has left the nav with a little delay
   */
  onMouseLeaveSubnav() {
    if (!this.menuSubnavBtns) return;

    clearTimeout(this.timer);

    this.timer = setTimeout(() => {
      this.updateSubNav(null, false);
    }, this.delay);
  }

  /**
   * @param {Event} event - looks for event.type for mouseover and click
   * passes event to handleDropdown function and fired with the mouseover event
   */
  onMouseOverSubnav(event) {
    clearTimeout(this.timer);

    this.timer = setTimeout(() => {
      this.onHandleDropdown(event);
    }, 50);
  }


  /**
   * @param {Event} event - looks for event.type for mouseover and click
   * Grabs the closest subnav button with event delegation
   * checks for various scenarios of hovering, click/keyboard behaviors
   */
  onHandleDropdown(event) {
    if (!this.menuSubnavBtns) return;

    // grabs nearest subnav button
    let button = event.target.closest('[data-element="subnav-button"]');

    // sets focusTrap to false by default
    let trap = false;

    if (event.type === 'mouseover') {
      // remove latest subnav if user is hovering over a link that does not have a subnav
      if (!button && (event.target.classList.contains('js-site-nav__button'))) {
        this.onMouseLeaveSubnav();
        return;
      } else if (!button || document.documentElement.classList.contains('menu-active')) {
        // don't do anything if user is still hovering over nav when subnav button isn't particularly near
        // this prevents the subnav from closing when user is hovering over a subnav
        return;
      }
    } else {
      if (!button) return;
    }

    if (event.type === 'click') {
      // Focus trap is needed
      trap = true;
      // If same button clicked, close if was expanded
      if (button === this.expandedSubNav) {
        // returns a boolean
        const wasExpanded = button.getAttribute('aria-expanded') === 'true';

        // if true, set button to null or keep button element as is
        button = wasExpanded ? null : button;
      }
    }

    // pass button (element or null) with trap argument (true or false)
    this.updateSubNav(button, trap);
  }

  /**
   * @param {Event} event - looks for event.type for mouseover and click
   * passes event to handleDropdown function and fired with the click event
   */
  onClickSubnav(event) {
    this.onHandleDropdown(event);
  }

  /**
   * @param {Event} event - looks for event.type for mouseover and click
   * @param {String} mode - "desktop" | "mobile"
   * Closes navs from escape key
   * And closes the nav if a user clicks outside of the nav
   */
  onCloseNav(event, mode) {
    if (event.type === 'keydown' && event.key == 'Escape') {
      this.initHideDropdowns(mode);
    } else if (event.type === 'click') {
      const clickOutside = !event.target.closest('[data-element="menu"]');
      const isLinkClick = event.target.closest('a');
      if (clickOutside || isLinkClick) {
        this.initHideDropdowns(mode);
      }
    }
  }


  /**
   * @param {HTMLElement} container - HTML element that holds all the links
   * Manages the focus state of the subnavs and parent nav
   */
  trapFocus(container) {
    const trapContainer = container;
    const focusableEls = [...trapContainer.querySelectorAll('a')];
    const firstFocus = focusableEls[0];
    const focusableElsLength = focusableEls.length;

    firstFocus.focus();

    let index = 0;

    const onMenuKeyDown = (event) => {
      // don't bubble events because focus will jump from the list of focused items
      event.preventDefault();
      event.stopPropagation();

      // if (event.key === 'Escape' && !this.isTransitioning) {

      if (event.key === 'Escape') {
        container.previousElementSibling.focus();
        this.updateSubNav(null);
        trapContainer.removeEventListener('keydown', onMenuKeyDown);
      }

      // If the home button is pressed
      // focus on the first focuable element
      if (event.key === 'Home') {
        firstFocus.focus();
      }

      // If the End button is pressed
      // focus on the last focusable element
      if (event.key === 'End') {
        focusableEls[focusableElsLength - 1].focus();
      }

      if (event.key === 'Tab' || event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        // shift + tab
        // moves the tab focus back one, or if the index is at the beginning, moves it to the last item
        if (event.shiftKey || event.key === 'ArrowUp') {
          index = (index === 0) ? (focusableElsLength - 1) : index -= 1;

          focusableEls[index].focus();
        } else {
          // moves the focus to the next link, if the
          // index is on the last item, moves it to the beginning
          index = (index + 1) % focusableElsLength;

          focusableEls[index].focus();
        }
      }
    };

    trapContainer.addEventListener('keydown', onMenuKeyDown);
  }

  /**
   * Sets a timeout for CSS styles if needed
   * Closes menus and refocuses focus
   */
  animateHideMenu() {
    this.hideMenu('mobile');

    const transitionComplete = () => {
      setTimeout(() => {
        this.menuBtn.focus();
      }, 25);
    };

    this.menuBody.addEventListener('transitionend', transitionComplete);
  }


  /**
   * Toggles menu based on this.show variable
   *
   */
  toggleMenu() {
    if (this.show) {
      this.showMenu();
    } else {
      this.animateHideMenu();
    }
  }
}

const menu = document.querySelector('[data-element="menu"]');

if (menu) {
  new MainMenu(menu);
}
